import React, { ReactElement } from 'react'
import { Spinner, ThemeUIStyleObject } from 'theme-ui'
import { ButtonProps, Button } from 'ui'
import { Flex } from '@theme-ui/components'
import { useHotkeys } from 'react-hotkeys-hook'

export default function ButtonWithLoadingState({
  isLoading = false,
  sxSpinner,
  children,
  disabled,
  variant = 'secondary',
  shouldDisplaySpinnerAndText,
  buttonRef,
  spinnerColor,
  onClickOrPressEnter,
  onClick,
  ...rest
}: ButtonProps & {
  isLoading?: boolean
  sxSpinner?: ThemeUIStyleObject
  shouldDisplaySpinnerAndText?: boolean
  spinnerColor?: string
  onClickOrPressEnter?: () => void
}): ReactElement {
  /*
   The composition of our components and elements within the form does not always trigger the onSubmit event,
   as certain requirements must be for this event to work. It is important for us to have a behavior similar to the onSubmit event,
   but at the same time not to be tied to the form and to be able to pass parameters instead of the event,
   as for a regular callback function.
  */
  useHotkeys(
    'Enter',
    event => {
      if (!(event.target as HTMLElement)?.id.includes('react-select')) {
        onClickOrPressEnter?.()
      }
    },
    {
      ignoreEventWhen: () => !onClickOrPressEnter || disabled === undefined || disabled,
      enableOnFormTags: ['input', 'textarea', 'select'],
    },
  )

  return (
    <Button
      ref={buttonRef}
      disabled={isLoading || disabled}
      variant={variant}
      onClick={onClickOrPressEnter || onClick}
      {...rest}
    >
      {isLoading ? (
        <Flex sx={{ alignItems: 'center', gap: '0.4rem' }}>
          <Spinner
            data-testid="button-loading-spinner"
            color={spinnerColor || 'aliceBlue'}
            sx={{ width: '2rem', height: '2rem', ...sxSpinner }}
          />
          {shouldDisplaySpinnerAndText && children}
        </Flex>
      ) : (
        children
      )}
    </Button>
  )
}
